import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { startWith, map, tap } from 'rxjs/operators';

import { Licence } from '@classes/licence/licence';
import { Organism } from '@classes/organism/organism';
import { Language } from '@classes/language';
import { LicenceType } from '@classes/licence/licence-type';
import { OrganismService } from '@services/organism.service';
import { LicenceService } from '@services/licence.service';
import { UiService } from '@services/ui.service';
import { User } from '@classes/licence/user';
import { LicenceTypeCode } from '@enums/licence-type-code.enum';
import { LicenceTerm } from '@classes/licence/licence-term';
import { UserService } from '@services/user.service';
import { DateAdapter } from '@angular/material/core';
import { LicenceAdress } from '@classes/licence/licence-address';

@Component({
    selector: 'app-licence-edit',
    templateUrl: './licence-edit.component.html',
    styleUrls: ['./licence-edit.component.scss']
})
export class LicenceEditComponent implements OnInit, OnDestroy {
    @Input() licence: Licence;
    @Output() editFinished = new EventEmitter<void>();

    private loadingSub: Subscription;

    licenceForm: FormGroup;

    organismList: Organism[];
    languageList: Language[];
    licenceTypes: LicenceType[];
    bankers: User[];
    filteredOrganisms: Observable<Organism[]>;
    licenceTerms: LicenceTerm[];
    municipalityList: Organism[];
    municipalities: Observable<Organism[]>;

    isLoading: boolean;
    isFormInit: boolean;
    isTestLicence: boolean = false;
    hasTerminationDate: boolean = false;

    constructor(
        private fb: FormBuilder,
        private organismSrv: OrganismService,
        private licenceSrv: LicenceService,
        private uiSrv: UiService,
        private userSrv: UserService,
        private dateAdapter: DateAdapter<any>
    ) { }

    ngOnInit() {
        this.initSubscription();

        this.userSrv.getParameters().pipe(
            tap(userParameters => {
                switch (userParameters.language.code)
                {
                    case "french": this.dateAdapter.setLocale('fr-BE'); break;
                    case "dutch" : this.dateAdapter.setLocale('nl-BE'); break;
                }
            })
        );
    }

    onSubmit(): void {
        this.isLoading = true;

        if (this.licence) {
            this.updateLicence();
        } else {
            this.createLicence();
        }
    }

    onCancel(): void {
        this.editFinished.emit();
    }

    displayFn(origin?: Organism): string | undefined {
        return origin ? origin.text.fullLabel : undefined;
    }

    private updateLicence(): void {
        const formValues = this.licenceForm.value;
        const licenceCopy: Licence = JSON.parse(JSON.stringify(this.licence));
        licenceCopy.text.shortLabel = formValues.name;
        licenceCopy.text.fullLabel = formValues.name;
        licenceCopy.language = this.languageList.find(l => l.code === formValues.lang);
        licenceCopy.licenceType = this.licenceTypes.find(t => t.code === formValues.licenceType);
        licenceCopy.organism.isPublic = formValues.isPublic;
        licenceCopy.communication = formValues.communication;
        licenceCopy.accountNumber = formValues.accountNumber;
        licenceCopy.businessNumber = formValues.businessNumber;
        licenceCopy.banker = this.bankers.find(b => b.username === formValues.banker);
        licenceCopy.endDate = formValues.endDate;
        licenceCopy.terminationDate = formValues.terminationDate;
        licenceCopy.isAutomaticallyDebited = formValues.isAutomaticallyDebited;
        licenceCopy.address = new LicenceAdress(formValues.addressStreet, formValues.addressNumber,
            formValues.addressBox, formValues.addressPostalCode, formValues.addressMunicipality);

        switch (formValues.billingMonth)
        {
            case 1 : licenceCopy.billingMonth = 'april'  ; break;
            case 2 : licenceCopy.billingMonth = 'october'; break;
            default: licenceCopy.billingMonth = ''       ; break;
        }

        this.licenceSrv.put(licenceCopy).subscribe(() => this.isLoading = false);
    }

    private createLicence(): void {
        const formValues = this.licenceForm.value;
        this.licence = new Licence(
            formValues.name,
            formValues.organism,
            this.languageList.find(l => l.code === formValues.lang),
            this.licenceTypes.find(t => t.code === formValues.licenceType),
            formValues.isPublic,
            formValues.communication,
            formValues.isAutomaticallyDebited,
            formValues.accountNumber,
            formValues.businessNumber,
            new LicenceAdress(formValues.addressStreet, formValues.addressNumber,
                formValues.addressBox, formValues.addressPostalCode, formValues.addressMunicipality),
            this.bankers.find(b => b.username === formValues.banker),
            formValues.licenceTerm,
            formValues.billingMonth
        );
        this.licence.endDate = this.licence.licenceType.code == 'test' ? null : formValues.endDate;

        this.licenceSrv.post(this.licence)
            .subscribe(() => {
                this.isLoading = false;
                this.editFinished.emit();
            });
    }

    private initSubscription(): void {
        this.isLoading = true;

        this.organismSrv.getList({isTest: false})
        .subscribe(municipalities => {
            this.municipalityList = municipalities;

            this.organismSrv.getList({isTest: true})
                .subscribe(organismList => {
                    this.organismList = organismList;

                    this.organismSrv.getList({withLicence: false, isTest: false})
                        .subscribe(organismList => {
                            this.organismList = this.organismList.concat(organismList);

                            this.licenceSrv.getDropdownsContent()
                            .subscribe(dropdownsContent => {
                                this.languageList = [ ...dropdownsContent.languages ];
                                this.licenceTypes = [ ...dropdownsContent.licenceTypes ];
                                this.bankers = [ ...dropdownsContent.bankers ];

                                this.initForm();
                                this.isLoading = false;
                            });
                        });
                });
            });

            this.loadingSub = this.uiSrv.loadingStateChanged
                .subscribe(loadingState => {
                    this.isLoading = loadingState;
                });

        const year  = this.uiSrv.translate('users.licenceForm.licenceTerm.year.shortLabel' );
        const years = this.uiSrv.translate('users.licenceForm.licenceTerm.years.shortLabel');
        this.licenceTerms = [{ yearTerm: 1, label: "1 " + year }];
        for (let i = 2; i <= 5; ++i)
            this.licenceTerms.push({ yearTerm: i, label: `${i} ${years}`});
    }

    private initForm(): void {
        let organism = null;
        let name = '';
        let lang = '';
        let licenceType = '';
        let isPublic = true;
        let communication = '';
        let isAutomaticallyDebited: boolean;
        let accountNumber = '';
        let businessNumber = '';
        let addressStreet = '';
        let addressNumber = '';
        let addressBox = '';
        let addressPostalCode = '';
        let addressMunicipality = '';
        let banker = '';
        let endDate: Date;
        let terminationDate: Date;
        let licenceTerm = 3;
        let billingMonth = 0;

        if (this.licence) {
            organism = this.licence.organism.text.shortLabel;
            name = this.licence.text.shortLabel;
            lang = this.licence.language.code;
            licenceType = this.licence.licenceType.code;
            isPublic = this.licence.organism.isPublic || false;
            communication = this.licence.communication;
            isAutomaticallyDebited = this.licence.isAutomaticallyDebited || false;
            accountNumber = this.licence.accountNumber;
            businessNumber = this.licence.businessNumber;
            addressStreet = this.licence.address.street;
            addressNumber = this.licence.address.number === '0' ? '' : this.licence.address.number;
            addressBox = this.licence.address.box;
            addressPostalCode = this.licence.address.postalCode;
            addressMunicipality = this.licence.address.municipality;
            banker = this.licence.banker ? this.licence.banker.username : '';
            endDate = this.licence.endDate;
            terminationDate = this.licence.terminationDate;

            switch (this.licence.billingMonth)
            {
                case 'april'  : billingMonth = 1; break;
                case 'october': billingMonth = 2; break;
                default       : billingMonth = 0; break;
            }
        }

        this.licenceForm = this.fb.group({
            organism: [{ value: organism, disabled: !!this.licence },
                [
                    Validators.required,
                    this.forbiddenTypeValidator('string')
                ]
            ],
            name: [name, Validators.required],
            lang: [lang, Validators.required],
            licenceType: [licenceType, Validators.required],
            isPublic: [isPublic],
            communication: [communication],
            isAutomaticallyDebited: [isAutomaticallyDebited],
            accountNumber: [accountNumber, [Validators.minLength(16), Validators.maxLength(16)]],
            businessNumber: [businessNumber, [Validators.minLength(10), Validators.maxLength(10)]],
            addressStreet: [addressStreet],
            addressNumber: [addressNumber, [Validators.pattern("^[1-9][0-9]*$")]],
            addressBox: [addressBox],
            addressPostalCode: [addressPostalCode, [Validators.pattern("^[0-9]{4}$")]],
            addressMunicipality: [addressMunicipality],
            banker: [banker],
            endDate: [endDate],
            terminationDate: [terminationDate],
            licenceTerm: [licenceTerm],
            billingMonth: [billingMonth]
        });

        if (licenceType === LicenceTypeCode.test) {
            this.licenceForm.controls.isPublic.setValue(false);
            this.licenceForm.controls.isPublic.disable();
            this.licenceForm.controls.terminationDate.setValue(null);
            this.licenceForm.controls.terminationDate.disable();
            this.licenceForm.controls.isAutomaticallyDebited.setValue(false);
            this.licenceForm.controls.isAutomaticallyDebited.disable();
        }

        this.hasTerminationDate = !!this.licenceForm.controls.terminationDate.value;

        this.isFormInit = true;
        this.setfilteredOrganisms();
        this.setMunicipalities();
        this.observeFormChanges();
    }

    private observeFormChanges(): void {
        this.licenceForm.controls.licenceType.valueChanges
        .subscribe((selType: LicenceTypeCode) => {
            this.isTestLicence = selType === LicenceTypeCode.test;
            if(selType === LicenceTypeCode.test) {
                this.licenceForm.controls.isPublic.setValue(false);
                this.licenceForm.controls.isPublic.disable();
                this.licenceForm.controls.terminationDate.setValue(null);
                this.licenceForm.controls.terminationDate.disable();
                this.licenceForm.controls.isAutomaticallyDebited.setValue(false);
                this.licenceForm.controls.isAutomaticallyDebited.disable();
            } else {
                this.licenceForm.controls.isPublic.enable();
                this.licenceForm.controls.terminationDate.enable();
                this.licenceForm.controls.isAutomaticallyDebited.enable();
            }
        });

        this.licenceForm.controls.terminationDate.valueChanges
        .subscribe((terminationDate?: Date) => {
            this.hasTerminationDate = !!terminationDate;
        });

    }

    private setfilteredOrganisms(): void {
        this.filteredOrganisms = this.licenceForm.controls.organism.valueChanges
            .pipe(
                startWith<string | Organism>(''),
                map(value => {
                    if (typeof value === 'string') {
                        return value;
                    } else {
                        return value.text.fullLabel;
                    }
                }),
                map(name => name ? this._filter(name) : this.organismList.slice())
            );
    }
    private setMunicipalities(): void {
        this.municipalities = this.licenceForm.controls.addressMunicipality.valueChanges
            .pipe(
                startWith<string | Organism>(''),
                map(value => {
                    if (typeof value === 'string') {
                        return value;
                    } else {
                        return value.text.fullLabel;
                    }
                }),
                map(name => name ? this._filterMunicipalityList(name) : this.municipalityList.slice())
            );
    }

    private _filter(value: string): Organism[] {
        const filterValue = value.toLowerCase();

        return this.organismList.filter(option => option.text.fullLabel.toLowerCase().includes(filterValue));
    }
    private _filterMunicipalityList(value: string): Organism[] {
        const filterValue = value.toLowerCase();

        return this.municipalityList.filter(option => option.text.fullLabel.toLowerCase().includes(filterValue));
    }

    private forbiddenTypeValidator(forbiddenType: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const forbidden = typeof control.value === forbiddenType;
            return forbidden ? { 'forbiddenType': { value: control.value } } : null;
        };
    }

    clearTerminationDate(): void {
        this.licenceForm.controls.terminationDate.setValue(null);
    }

    ngOnDestroy(): void {
        if(this.loadingSub) {
            this.loadingSub.unsubscribe();
        }
    }
}
